import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AnalyzeQuotaInfo } from '../../../components/billing/AnalyzeQuotaModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useHousehold } from '../../../contexts/HouseholdContext';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { usePortfolio } from '../../../contexts/PortfolioContext';
import { forcePullDashboards } from '../../../services/analyticsService';
import { telemetry } from '../../../services/telemetry';
import { ApiError, isAnalyzeQuotaError } from '../../../services/apiClient';
import { getImport, startImport, uploadImportImage } from '../../../services/importService';
import { extractPortfolioFromScreenshot } from '../../../services/portfolioImportService';
import { listTransactions } from '../../../services/transactionService';
import {
  ImportWebSocketSession,
  isImportWebSocketConfigured,
} from '../../../services/importWebSocket';
import type {
  ImportCategoryBreakdownItem,
  ImportFileError,
  ImportLocalImage,
  ImportMonthSection,
  ImportProgress,
  ImportWsEvent,
  ImportWsTransaction,
} from '../../../types/import';
import { applyPortfolioImportResult } from '../../../utils/applyPortfolioImport';
import { authService } from '../../Auth/services/authService';
import {
  buildImageParsedStatusMessage,
  deriveCategoryBreakdown,
  deriveMonthSections,
  formatFileFailedMessage,
  formatPartialImportStatus,
  newestMonthHint,
  sortTxNewestFirst,
  upsertFileError,
} from '../utils/importDerivedState';
import { isPdfMime } from '../utils/imageHelpers';

function formatResetLabel(iso?: string): string | undefined {
  if (!iso) return undefined;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return undefined;
  }
}

function quotaInfoFromError(error: ApiError): AnalyzeQuotaInfo {
  return {
    message: error.message,
    upgradeRequired: error.upgradeRequired === true,
    monthlyLimitReached: error.monthlyLimitReached === true,
    remaining: error.remaining,
    limit: error.limit,
    periodResetsAt: error.periodResetsAt,
  };
}

function quotaInfoFromRemaining(
  remaining: number,
  limit: number | undefined,
  requested: number,
  plan: string | undefined,
  periodResetsAt?: string,
): AnalyzeQuotaInfo {
  const isPro = plan === 'pro';
  const resetLabel = formatResetLabel(periodResetsAt);
  if (remaining <= 0) {
    return {
      message: isPro
        ? `You've reached your Pro AI analysis limit for this billing period.${resetLabel ? ` Quota resets on ${resetLabel}.` : ' Quota resets on your next billing day.'}`
        : `You've used all ${limit ?? 5} free AI analyses. Upgrade to Pro for household, AI assistant, and more analyses.`,
      upgradeRequired: !isPro,
      monthlyLimitReached: isPro,
      remaining: 0,
      limit,
      periodResetsAt,
    };
  }
  return {
    message: `This upload has ${requested} files but you only have ${remaining} AI analysis${remaining === 1 ? '' : 'es'} left.`,
    upgradeRequired: !isPro,
    monthlyLimitReached: isPro,
    remaining,
    limit,
    periodResetsAt,
  };
}

const POLL_INTERVAL_MS = 4_000;

const initialProgress = (): ImportProgress => ({
  phase: 'idle',
  uploadIndex: 0,
  uploadTotal: 0,
  processedImages: 0,
  totalImages: 0,
  transactionCount: 0,
  duplicateCount: 0,
  transactions: [],
  importBatchId: null,
  recentMerchants: [],
  statusMessage: '',
  error: null,
  fileErrors: [],
});

function appendMerchants(existing: string[], txs: ImportWsTransaction[]): string[] {
  const next = [...existing];
  for (const tx of txs) {
    if (tx.merchant && !next.includes(tx.merchant)) {
      next.push(tx.merchant);
    }
    if (next.length >= 8) break;
  }
  return next;
}

function mapApiToImportTx(tx: {
  date: string | Date;
  amount: number;
  direction: 'debit' | 'credit';
  kind: string;
  tab: 'spend' | 'wealth';
  merchant: string;
  categoryName?: string;
  description?: string;
  confidence?: number;
}): ImportWsTransaction {
  const dateRaw = tx.date as unknown;
  const dateStr =
    typeof dateRaw === 'string'
      ? dateRaw
      : dateRaw instanceof Date
        ? dateRaw.toISOString()
        : String(dateRaw);
  return {
    date: dateStr.slice(0, 10),
    amount: Math.abs(tx.amount),
    direction: tx.direction,
    kind: tx.kind,
    tab: tx.tab,
    merchant: tx.merchant,
    categoryName: tx.categoryName || 'Other',
    description: tx.description,
    confidence: tx.confidence,
  };
}

async function fetchAllImportBatchTxs(
  accessToken: string,
  importBatchId: string,
): Promise<ImportWsTransaction[]> {
  const mapped: ImportWsTransaction[] = [];
  let cursor: string | null | undefined;
  do {
    const page = await listTransactions({
      accessToken,
      importBatchId,
      limit: 100,
      cursor,
    });
    mapped.push(...page.transactions.map(mapApiToImportTx));
    cursor = page.hasMore ? page.nextCursor : null;
  } while (cursor);
  return sortTxNewestFirst(mapped);
}

/**
 * Import orchestration (upload / WS / poll / progress).
 * Must live in ImportOnboardingProvider so dismissing the modal does not tear it down.
 */
export function useImportFlow() {
  const { tokens, user, refreshUser } = useAuth();
  const { draft, updateDraft } = useOnboarding();
  const { setCore, getCore } = useHousehold();
  const { applySnapshot, refresh: refreshPortfolio } = usePortfolio();
  const [images, setImages] = useState<ImportLocalImage[]>([]);
  const [progress, setProgress] = useState<ImportProgress>(initialProgress);
  const [quotaModal, setQuotaModal] = useState<AnalyzeQuotaInfo | null>(null);
  const sessionRef = useRef<ImportWebSocketSession | null>(null);
  const activeImportIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);
  const completeHandledRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Last tx count applied from poll list refetch — avoid redundant list calls. */
  const lastPolledTxCountRef = useRef(0);
  const transactionsLenRef = useRef(0);

  useEffect(() => {
    transactionsLenRef.current = progress.transactions.length;
  }, [progress.transactions.length]);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const disposeSession = useCallback(() => {
    clearPoll();
    sessionRef.current?.dispose();
    sessionRef.current = null;
  }, [clearPoll]);

  const dismissQuotaModal = useCallback(() => {
    setQuotaModal(null);
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    completeHandledRef.current = false;
    activeImportIdRef.current = null;
    lastPolledTxCountRef.current = 0;
    disposeSession();
    setQuotaModal(null);
    setImages((prev) => {
      prev.forEach((img) => {
        if (img.previewUri?.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUri);
        }
      });
      return [];
    });
    setProgress(initialProgress());
    cancelledRef.current = false;
  }, [disposeSession]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      disposeSession();
    };
  }, [disposeSession]);

  const addImages = useCallback((next: ImportLocalImage[]) => {
    if (!next.length) return;
    setImages((prev) => [...prev, ...next].slice(0, 20));
    setProgress((p) => ({ ...p, error: null, phase: p.phase === 'failed' ? 'idle' : p.phase }));
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target?.previewUri?.startsWith('blob:')) {
        URL.revokeObjectURL(target.previewUri);
      }
      return prev.filter((img) => img.id !== id);
    });
  }, []);

  const markOnboardingComplete = useCallback(async () => {
    await authService.updateProfile({ onboardingComplete: true });
    await refreshUser();
  }, [refreshUser]);

  // If terminal success set phase to complete but the profile flag lagged (minimize /
  // cancelledRef races / network blip), retry until the user reflects complete.
  useEffect(() => {
    if (progress.phase !== 'complete') return;
    if (!user || user.onboardingComplete === true) return;
    void markOnboardingComplete().catch((error) => {
      console.error('[ImportOnboarding] ❌ Retry mark onboarding complete failed', error);
    });
  }, [markOnboardingComplete, progress.phase, user]);

  const handleTerminalSuccess = useCallback(
    async (
      transactionCount: number,
      duplicateCount: number,
      processedImages: number,
      totalImages: number,
      incomingFileErrors: ImportFileError[] = [],
    ) => {
      // Never gate on cancelledRef / modal visibility — server success must mark onboarding.
      if (completeHandledRef.current) return;
      completeHandledRef.current = true;
      const importBatchId = activeImportIdRef.current;
      disposeSession();

      setProgress((p) => {
        const fileErrors = incomingFileErrors.reduce(
          (acc, entry) => upsertFileError(acc, entry),
          p.fileErrors ?? [],
        );
        const total = totalImages || p.totalImages;
        const partial =
          (total > 0 && processedImages < total) || fileErrors.length > 0;
        const partialMessage = formatPartialImportStatus(processedImages, total);
        return {
          ...p,
          phase: 'complete',
          importBatchId: importBatchId ?? p.importBatchId,
          transactionCount,
          duplicateCount,
          processedImages,
          totalImages: total,
          fileErrors,
          statusMessage: partial
            ? partialMessage ||
              (fileErrors[0]
                ? formatFileFailedMessage(fileErrors[0])
                : 'Some statements failed — re-upload that PDF')
            : transactionCount > 0
              ? `Imported ${transactionCount} transaction${transactionCount === 1 ? '' : 's'}`
              : 'Import finished — no new transactions',
          error: partial
            ? partialMessage || fileErrors[0]?.error || p.error
            : null,
        };
      });

      // Flag first and independently of UI refresh / tx list — survives minimize and pull failures.
      try {
        await markOnboardingComplete();
      } catch (error) {
        console.error('[ImportOnboarding] ❌ Failed to mark onboarding complete', error);
        // Still refresh so seeded take-home from salary import appears on Home.
        try {
          await refreshUser();
        } catch (refreshError) {
          console.warn('[ImportOnboarding] refreshUser after import failed', refreshError);
        }
      }

      if (importBatchId && tokens?.accessToken && transactionCount > 0) {
        try {
          const sorted = await fetchAllImportBatchTxs(tokens.accessToken, importBatchId);
          console.info('[ImportOnboarding] 📋 Loaded import batch txs', {
            importBatchId,
            count: sorted.length,
          });
          setProgress((p) => ({
            ...p,
            transactions: sorted,
            transactionCount: sorted.length || transactionCount,
            recentMerchants: sorted.slice(0, 8).map((t) => t.merchant),
          }));
        } catch (error) {
          console.warn('[ImportOnboarding] Failed to load imported tx list', error);
        }
      }

      activeImportIdRef.current = null;

      try {
        await forcePullDashboards({
          userId: user?.id,
          reason: 'import_complete',
        });
      } catch (error) {
        console.warn('[ImportOnboarding] Dashboard pull after import failed', error);
        try {
          await forcePullDashboards({
            userId: user?.id,
            reason: 'import_complete_fallback',
          });
        } catch (fallbackError) {
          console.warn('[ImportOnboarding] Dashboard pull fallback failed', fallbackError);
        }
      }
    },
    [disposeSession, markOnboardingComplete, refreshUser, tokens?.accessToken, user?.id],
  );

  const handleTerminalFailure = useCallback(
    (message: string) => {
      if (completeHandledRef.current) return;
      completeHandledRef.current = true;
      activeImportIdRef.current = null;
      disposeSession();
      setProgress((p) => ({
        ...p,
        phase: 'failed',
        error: message,
        statusMessage: 'Import failed',
      }));
    },
    [disposeSession],
  );

  const applyWsEvent = useCallback(
    (event: ImportWsEvent) => {
      if (event.event === 'pong' || event.event === 'subscribed') return;

      // Terminal events must reach markOnboardingComplete even if UI was torn down / cancelled.
      const isTerminal = event.event === 'import_complete' || event.event === 'import_failed';
      if (cancelledRef.current && !isTerminal) return;

      console.info('[ImportOnboarding] 📥 WS → UI', {
        event: event.event,
        importBatchId: 'importBatchId' in event ? event.importBatchId : undefined,
        ...(event.event === 'image_parsed'
          ? {
              inserted: event.inserted,
              duplicateCount: event.duplicateCount,
              runningTransactionCount: event.runningTransactionCount,
            }
          : {}),
        ...(event.event === 'import_complete'
          ? {
              transactionCount: event.transactionCount,
              duplicateCount: event.duplicateCount,
            }
          : {}),
      });

      switch (event.event) {
        case 'import_started':
          setProgress((p) => ({
            ...p,
            phase: 'processing',
            totalImages: event.totalImages,
            fileErrors: [],
            error: null,
            statusMessage: 'Queued — starting analysis…',
          }));
          break;
        case 'image_started':
          setProgress((p) => {
            const latestFail = (p.fileErrors ?? []).at(-1);
            return {
              ...p,
              phase: 'processing',
              totalImages: event.totalImages,
              processedImages: event.imageIndex,
              statusMessage: latestFail
                ? formatFileFailedMessage(latestFail)
                : `Analyzing file ${event.imageIndex + 1} of ${event.totalImages}…`,
            };
          });
          break;
        case 'image_parsed':
          setProgress((p) => {
            const transactions = sortTxNewestFirst([...p.transactions, ...event.transactions]);
            lastPolledTxCountRef.current = Math.max(
              lastPolledTxCountRef.current,
              event.runningTransactionCount,
              transactions.length,
            );
            const latestFail = (p.fileErrors ?? []).at(-1);
            return {
              ...p,
              phase: 'processing',
              importBatchId: event.importBatchId || p.importBatchId,
              totalImages: event.totalImages,
              processedImages: event.imageIndex + 1,
              transactionCount: event.runningTransactionCount,
              duplicateCount: p.duplicateCount + event.duplicateCount,
              transactions,
              recentMerchants: appendMerchants(p.recentMerchants, event.transactions),
              statusMessage: latestFail
                ? formatFileFailedMessage(latestFail)
                : buildImageParsedStatusMessage(
                    transactions,
                    event.runningTransactionCount,
                    event.transactions,
                  ),
            };
          });
          if (event.inserted > 0) {
            void forcePullDashboards({
              userId: user?.id,
              reason: `ws_image_parsed_${event.imageIndex}`,
            });
          }
          break;
        case 'image_failed':
          setProgress((p) => {
            const nextError = {
              imageIndex: event.imageIndex,
              s3Key: event.s3Key,
              error: event.error || 'could not parse',
            };
            const fileErrors = upsertFileError(p.fileErrors ?? [], nextError);
            return {
              ...p,
              fileErrors,
              statusMessage: formatFileFailedMessage(nextError),
              error: nextError.error,
            };
          });
          break;
        case 'import_complete':
          void handleTerminalSuccess(
            event.transactionCount,
            event.duplicateCount,
            event.processedImages,
            event.totalImages,
            event.fileErrors ?? [],
          );
          break;
        case 'import_failed':
          handleTerminalFailure(event.error || 'Import failed');
          break;
        default:
          break;
      }
    },
    [handleTerminalFailure, handleTerminalSuccess, user?.id],
  );

  /** Poll GET /imports/{id}; when tx count advances, also listTransactions for live list. */
  const syncImportOnce = useCallback(
    async (importId: string) => {
      if (completeHandledRef.current) return;
      try {
        const { import: batch } = await getImport(importId);
        if (completeHandledRef.current) return;

        // Terminal poll results always run — onboarding flag must not depend on cancelledRef.
        if (batch.status === 'completed') {
          console.info('[ImportOnboarding] 📡 Poll → complete', {
            importId,
            transactionCount: batch.transactionCount,
            duplicateCount: batch.duplicateCount,
            processedImages: batch.processedImages,
            failedFiles: batch.fileErrors?.length ?? 0,
          });
          await handleTerminalSuccess(
            batch.transactionCount,
            batch.duplicateCount,
            batch.processedImages,
            batch.imageKeys.length,
            batch.fileErrors ?? [],
          );
          return;
        }

        if (batch.status === 'failed') {
          console.warn('[ImportOnboarding] 📡 Poll → failed', {
            importId,
            error: batch.error,
          });
          handleTerminalFailure(batch.error || 'Import failed');
          return;
        }

        if (cancelledRef.current) return;

        console.info('[ImportOnboarding] 📡 Poll → status', {
          importId,
          status: batch.status,
          processedImages: batch.processedImages,
          transactionCount: batch.transactionCount,
          duplicateCount: batch.duplicateCount,
        });

        const prevTxCount = lastPolledTxCountRef.current;
        const shouldRefetchTxs =
          batch.transactionCount > prevTxCount ||
          (batch.transactionCount > 0 && transactionsLenRef.current === 0);

        let listed: ImportWsTransaction[] | null = null;
        if (shouldRefetchTxs && tokens?.accessToken && batch.transactionCount > 0) {
          try {
            listed = await fetchAllImportBatchTxs(tokens.accessToken, importId);
            lastPolledTxCountRef.current = Math.max(
              batch.transactionCount,
              listed.length,
            );
            console.info('[ImportOnboarding] 📡 Poll → refreshed txs', {
              importId,
              count: listed.length,
              batchCount: batch.transactionCount,
            });
          } catch (error) {
            console.warn('[ImportOnboarding] poll tx list failed', error);
          }
        }

        setProgress((p) => {
          const transactions = listed ?? p.transactions;
          const month = newestMonthHint(transactions);
          const fileErrors = (batch.fileErrors ?? []).reduce(
            (acc, entry) => upsertFileError(acc, entry),
            p.fileErrors ?? [],
          );
          const latestFail = fileErrors[fileErrors.length - 1];
          const baseStatus =
            batch.status === 'pending'
              ? 'Uploaded — waiting in queue…'
              : latestFail
                ? formatFileFailedMessage(latestFail)
                : batch.processedImages > 0
                  ? `Analyzed ${batch.processedImages}/${batch.imageKeys.length} · ${batch.transactionCount} txs`
                  : 'Analyzing statement (this can take up to a minute)…';
          const statusMessage =
            !latestFail && month && batch.processedImages > 0 && batch.transactionCount > 0
              ? `${baseStatus} · ${month}`
              : baseStatus;

          return {
            ...p,
            phase: 'processing',
            processedImages: batch.processedImages,
            totalImages: batch.imageKeys.length,
            transactionCount: batch.transactionCount,
            duplicateCount: batch.duplicateCount,
            fileErrors,
            error: latestFail?.error ?? p.error,
            ...(listed
              ? {
                  transactions,
                  recentMerchants: transactions.slice(0, 8).map((t) => t.merchant),
                }
              : {}),
            statusMessage,
          };
        });

        // Keep Home/Spend charts fresh while the sheet is minimized (WS may be quiet).
        if (batch.transactionCount > prevTxCount) {
          void forcePullDashboards({
            userId: user?.id,
            reason: `poll_tx_advance_${importId}`,
          });
        }
      } catch (error) {
        console.warn('[ImportOnboarding] status sync failed', error);
      }
    },
    [handleTerminalFailure, handleTerminalSuccess, tokens?.accessToken, user?.id],
  );

  const startPolling = useCallback(
    (importId: string) => {
      clearPoll();
      void syncImportOnce(importId);
      pollTimerRef.current = setInterval(() => {
        void syncImportOnce(importId);
      }, POLL_INTERVAL_MS);
    },
    [clearPoll, syncImportOnce],
  );

  const ensureSession = useCallback(
    (accessToken: string) => {
      if (sessionRef.current) return sessionRef.current;

      const session = new ImportWebSocketSession({
        accessToken,
        onEvent: applyWsEvent,
        onUnexpectedClose: () => {
          const id = activeImportIdRef.current;
          // Still poll for terminal success so onboarding can be marked if WS drops.
          if (id && !completeHandledRef.current) {
            void syncImportOnce(id);
          }
        },
      });
      sessionRef.current = session;
      return session;
    },
    [applyWsEvent, syncImportOnce],
  );

  /** Best-effort: read brokerage portfolio balances from non-PDF screenshots. */
  const extractPortfolioFromImages = useCallback(
    async (localImages: ImportLocalImage[]) => {
      const screenshots = localImages.filter((img) => !isPdfMime(img.mimeType));
      if (!screenshots.length) return 0;

      let importedAccounts = 0;
      let draftAssets = draft.assets;
      let household = getCore();

      for (const image of screenshots.slice(0, 4)) {
        try {
          const result = await extractPortfolioFromScreenshot(image.dataUrl);
          if (result.snapshot) {
            applySnapshot(result.snapshot);
          }
          const applied = applyPortfolioImportResult({
            result,
            draftAssets,
            core: household,
          });
          if (!applied) continue;
          draftAssets = applied.nextDraftAssets;
          household = applied.nextCore;
          importedAccounts += applied.accountCount;
        } catch (error) {
          console.info('[ImportOnboarding] Portfolio extract skipped for image', {
            name: image.name,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      if (importedAccounts > 0) {
        await setCore(household);
        await updateDraft({
          assets: draftAssets,
          completedIntro: true,
        });
        void refreshPortfolio();
        setProgress((p) => ({
          ...p,
          statusMessage:
            p.phase === 'complete' || p.phase === 'processing'
              ? `${p.statusMessage.replace(/ · added \d+ investment accounts?/, '')} · added ${importedAccounts} investment account${importedAccounts === 1 ? '' : 's'}`
              : p.statusMessage,
        }));
      }

      return importedAccounts;
    },
    [applySnapshot, draft.assets, getCore, refreshPortfolio, setCore, updateDraft],
  );

  const beginImport = useCallback(async () => {
    if (!images.length) {
      setProgress((p) => ({ ...p, error: 'Add at least one screenshot or PDF to continue' }));
      return;
    }
    void telemetry.logFeature('import_start', { image_count: images.length });

    const accessToken = tokens?.accessToken;
    if (!accessToken) {
      handleTerminalFailure('Sign in required to import');
      return;
    }
    // Fresh quota before any S3 upload — show popup immediately when over limit.
    let profile = user;
    try {
      const fresh = await refreshUser();
      if (fresh) profile = fresh;
    } catch {
      // Use cached user if profile refresh fails.
    }
    const remaining = profile?.remainingAnalyzes;
    const limit = profile?.analyzeLimit;
    if (typeof remaining === 'number') {
      if (remaining <= 0 || images.length > remaining) {
        setQuotaModal(
          quotaInfoFromRemaining(
            remaining,
            limit,
            images.length,
            profile?.plan,
            profile?.analyzePeriodResetsAt,
          ),
        );
        setProgress((p) => ({
          ...p,
          phase: 'idle',
          error: null,
          statusMessage: '',
        }));
        return;
      }
    }

    cancelledRef.current = false;
    completeHandledRef.current = false;
    activeImportIdRef.current = null;
    lastPolledTxCountRef.current = 0;
    disposeSession();

    setProgress({
      ...initialProgress(),
      phase: 'uploading',
      uploadTotal: images.length,
      totalImages: images.length,
      statusMessage: `Uploading 1 of ${images.length} to cloud…`,
    });

    // Same upload batch: also try investment portfolio balances (Robinhood, etc.).
    const portfolioTask = extractPortfolioFromImages(images);

    try {
      const imageKeys: string[] = [];
      for (let i = 0; i < images.length; i += 1) {
        if (cancelledRef.current) return;
        setProgress((p) => ({
          ...p,
          phase: 'uploading',
          uploadIndex: i + 1,
          uploadTotal: images.length,
          statusMessage: `Uploading ${i + 1} of ${images.length} to cloud…`,
        }));
        const uploaded = await uploadImportImage(images[i].dataUrl);
        imageKeys.push(uploaded.s3Key);
      }

      const session = isImportWebSocketConfigured() ? ensureSession(accessToken) : null;
      if (!session) {
        console.warn(
          '[ImportOnboarding] BUDGET_WS_URL unset — import will complete via polling',
        );
      }

      setProgress((p) => ({
        ...p,
        phase: 'processing',
        uploadIndex: images.length,
        statusMessage: 'Upload done — reading spend & investments…',
      }));

      const { import: batch } = await startImport(imageKeys);
      activeImportIdRef.current = batch.id;
      console.info('[ImportOnboarding] 🚀 Import started', {
        importBatchId: batch.id,
        imageCount: imageKeys.length,
      });
      setProgress((p) => ({
        ...p,
        importBatchId: batch.id,
        transactions: [],
      }));
      session?.subscribe(batch.id);
      startPolling(batch.id);
      void refreshUser();

      void portfolioTask.then((count) => {
        if (count > 0) {
          console.info('[ImportOnboarding] 📈 Portfolio balances imported', { count });
        }
      });
    } catch (error) {
      if (isAnalyzeQuotaError(error)) {
        setQuotaModal(quotaInfoFromError(error));
        setProgress((p) => ({
          ...initialProgress(),
          phase: 'idle',
          error: null,
        }));
        void refreshUser();
        return;
      }
      const message = error instanceof Error ? error.message : 'Upload failed';
      handleTerminalFailure(message);
    }
  }, [
    disposeSession,
    ensureSession,
    extractPortfolioFromImages,
    handleTerminalFailure,
    images,
    refreshUser,
    startPolling,
    tokens?.accessToken,
    user?.analyzeLimit,
    user?.plan,
    user?.remainingAnalyzes,
  ]);

  const skipOnboarding = useCallback(async () => {
    await markOnboardingComplete();
  }, [markOnboardingComplete]);

  const busy = progress.phase === 'uploading' || progress.phase === 'processing';

  const categoryBreakdown: ImportCategoryBreakdownItem[] = useMemo(
    () => deriveCategoryBreakdown(progress.transactions),
    [progress.transactions],
  );

  const monthSections: ImportMonthSection[] = useMemo(
    () => deriveMonthSections(progress.transactions),
    [progress.transactions],
  );

  return {
    images,
    progress,
    busy,
    categoryBreakdown,
    monthSections,
    remainingAnalyzes: user?.remainingAnalyzes,
    analyzeLimit: user?.analyzeLimit,
    quotaModal,
    dismissQuotaModal,
    addImages,
    removeImage,
    beginImport,
    skipOnboarding,
    reset,
    markOnboardingComplete,
  };
}
